import dayjs from "dayjs";
import type { InsolvencyType } from "@/types/entities/insolvencies";

export interface BadgeConfig {
  badgeLabel: string;
  badgeVariant: "danger" | "success" | "warning" | "neutral";
}

export const getInsolvencyableName = (insolvency: InsolvencyType): string => {
  if (!insolvency.insolvencyable?.data) return "N/A";
  const { data } = insolvency.insolvencyable;
  if (insolvency.insolvencyable.type === "applicant") {
    return (
      `${data.first_name || ""} ${data.last_name || ""}`.trim() || "Sin nombre"
    );
  }

  return data.commercial_name || data.name || "Sin nombre";
};

export const getPaidOnBehalfOf = (
  element: InsolvencyType,
): string | undefined => {
  if (!element) return undefined;
  if (!element.payment?.paid_on_behalf_of) return undefined;
  // impersonation case, show the name of the person on behalf of who the payment was made
  return `${element.payment.paid_on_behalf_of.first_name ?? ""} ${element.payment.paid_on_behalf_of.last_name ?? ""}`.trim();
};

export const getHolder = (item: InsolvencyType) => {
  if (item.payment?.paid_by) {
    // impersonation case, show the name of the person who made the payment
    return `${item.payment.paid_by.first_name ?? ""} ${item.payment.paid_by.last_name ?? ""}`.trim();
  }
  return "N/A";
};

export const getInsolvencyableId = (insolvency: InsolvencyType): string => {
  if (!insolvency.insolvencyable?.data) return "N/A";

  const { data } = insolvency.insolvencyable;

  if (insolvency.insolvencyable.type === "applicant") {
    return data.email || "N/A";
  }
  if (insolvency.insolvencyable.type === "expedient_base_registry") {
    return data.unique_code || "N/A";
  }

  return data.code || "N/A";
};

export const getInsolvencyType = (
  insolvency: InsolvencyType,
): "personal" | "property" | "expedient" => {
  if (insolvency.insolvencyable?.type === "expedient_base_registry") {
    return "expedient";
  }
  return insolvency.insolvencyable?.type === "applicant"
    ? "personal"
    : "property";
};

export const parseAmount = (amount: string | number): number => {
  if (typeof amount === "number") return amount;
  return Number.parseFloat(amount) || 0;
};

export const getPersonInitial = (name: string) => {
  const trimmedName = name.trim();
  if (!trimmedName) {
    return "?";
  }

  return trimmedName.charAt(0).toUpperCase();
};

export const getDaysSinceCreated = (createdAt: string): number => {
  const today = dayjs();
  const createdDate = dayjs(createdAt);
  return today.diff(createdDate, "day");
};

export const getStatusConfig = (infraction: InsolvencyType): BadgeConfig => {
  const daysSinceCreated = getDaysSinceCreated(infraction.created_at);
  const totalDeadlineDays = convertUnitToDays(
    infraction.payment_deadline_value,
    infraction.payment_deadline_unit,
  );

  if (infraction.status === "ACTIVE") {
    // Check if payment deadline has passed
    if (totalDeadlineDays <= daysSinceCreated) {
      // Payment overdue - show danger (insolvencia)
      return {
        badgeLabel: "Insolvencia",
        badgeVariant: "danger",
      };
    } else {
      // Payment still has time - show warning
      return {
        badgeLabel: "Pendiente",
        badgeVariant: "warning",
      };
    }
  }

  // Both cancelled show as "Anulado"
  if (infraction.status === "CANCELLED") {
    return {
      badgeLabel: "Anulado",
      badgeVariant: "neutral",
    };
  }

  if (infraction.status === "SOLVENT") {
    return {
      badgeLabel: "Solvente",
      badgeVariant: "success",
    };
  }

  if (infraction.status === "APPEAL_SUBMITTED") {
    return {
      badgeLabel: "Apelación enviada",
      badgeVariant: "warning",
    };
  }

  if (infraction.status === "APPEAL_REJECTED") {
    return {
      badgeLabel: "Apelación rechazada",
      badgeVariant: "danger",
    };
  }

  // Fallback
  return {
    badgeLabel: "Desconocido",
    badgeVariant: "neutral",
  };
};

/**
 *  Gets the display text for a given time unit in Spanish.
 * @param unit
 * @returns
 */
export const getUnitText = (unit: string): string => {
  switch (unit?.toUpperCase()) {
    case "HOURS":
      return "horas";
    case "DAYS":
      return "días";
    case "WEEKS":
      return "semanas";
    case "MONTHS":
      return "meses";
    default:
      return "";
  }
};

/**
 *  Converts the given value and unit to days.
 * @param value
 * @param unit
 * @returns
 */
export const convertUnitToDays = (value: number, unit: string): number => {
  switch (unit?.toUpperCase()) {
    case "HOURS":
      return value / 24;
    case "DAYS":
      return value;
    case "WEEKS":
      return value * 7;
    case "MONTHS":
      return value * 30;
    default:
      return value;
  }
};

export const getPaymentMethodLabel = (
  method: string | null | undefined,
): string => {
  if (!method) return "-";
  switch (method) {
    case "CREDIT_CARD":
      return "Tarjeta de crédito";
    case "NPE":
      return "NPE";
    default:
      return method;
  }
};

/**
 * Check if insolvency should be disabled for selection
 * @param insolvency - The insolvency object
 * @returns True if insolvency cannot be selected for payment
 */
export const isInsolvencyDisabled = (insolvency: InsolvencyType): boolean => {
  // Disabled if status is SOLVENT or CANCELLED
  if (insolvency.status === "SOLVENT" || insolvency.status === "CANCELLED") {
    return true;
  }

  return false;
};
